import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Eye, Radio } from "lucide-react";
import "./HomeView.css";

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
  type: number;
};

function spawnParticle(w: number, h: number, fromTop: boolean): Particle {
  return {
    x: Math.random() * w,
    y: fromTop ? -20 : Math.random() * h,
    vx: (Math.random() - 0.5) * 0.4,
    vy: 0.3 + Math.random() * 0.8,
    size: 8 + Math.random() * 22,
    opacity: 0.06 + Math.random() * 0.14,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 0.6,
    type: Math.floor(Math.random() * 3),
  };
}

function drawSnowflake(ctx: CanvasRenderingContext2D, p: Particle) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate((p.rotation * Math.PI) / 180);
  ctx.globalAlpha = p.opacity;
  ctx.strokeStyle = "#5EEAD4";
  ctx.lineWidth = 1;
  ctx.lineCap = "round";

  const s = p.size / 2;
  const arms = 6;
  const armAngle = (Math.PI * 2) / arms;

  for (let i = 0; i < arms; i++) {
    const angle = armAngle * i;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(cos * s, sin * s);
    ctx.stroke();

    const branchLen = s * 0.35;
    const branchStart = s * 0.55;

    if (p.type === 0 || p.type === 2) {
      const bx = cos * branchStart;
      const by = sin * branchStart;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(angle + 0.5) * branchLen, by + Math.sin(angle + 0.5) * branchLen);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(angle - 0.5) * branchLen, by + Math.sin(angle - 0.5) * branchLen);
      ctx.stroke();
    }

    if (p.type === 1 || p.type === 2) {
      const branchStart2 = s * 0.78;
      const bx2 = cos * branchStart2;
      const by2 = sin * branchStart2;
      const bl2 = branchLen * 0.6;
      ctx.beginPath();
      ctx.moveTo(bx2, by2);
      ctx.lineTo(bx2 + Math.cos(angle + 0.4) * bl2, by2 + Math.sin(angle + 0.4) * bl2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(bx2, by2);
      ctx.lineTo(bx2 + Math.cos(angle - 0.4) * bl2, by2 + Math.sin(angle - 0.4) * bl2);
      ctx.stroke();
    }
  }

  ctx.restore();
}

const COUNT = 35;

export function HomeView() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bigSfRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef(0);
  const boundsRef = useRef({ w: 0, h: 0 });

  const tick = useCallback(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    if (rect.width !== boundsRef.current.w || rect.height !== boundsRef.current.h) {
      boundsRef.current = { w: rect.width, h: rect.height };
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    const { w, h } = boundsRef.current;
    const ps = particlesRef.current;

    ctx.clearRect(0, 0, w, h);

    for (let i = 0; i < ps.length; i++) {
      const p = ps[i];
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;

      if (p.y > h + 30 || p.x < -30 || p.x > w + 30) {
        ps[i] = spawnParticle(w, h, true);
      }

      drawSnowflake(ctx, p);
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    boundsRef.current = { w: rect.width, h: rect.height };
    canvas.width = rect.width;
    canvas.height = rect.height;

    particlesRef.current = Array.from({ length: COUNT }, () =>
      spawnParticle(rect.width, rect.height, false),
    );

    rafRef.current = requestAnimationFrame(tick);

    const onResize = () => {
      const r = container.getBoundingClientRect();
      boundsRef.current = { w: r.width, h: r.height };
      canvas.width = r.width;
      canvas.height = r.height;
    };
    window.addEventListener("resize", onResize);

    const container2 = containerRef.current;
    const bigSf = bigSfRef.current;
    let scrollRaf = 0;
    const onScroll = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        if (!container2 || !bigSf) return;
        const scrollTop = container2.scrollTop;
        const maxScroll = container2.scrollHeight - container2.clientHeight;
        const progress = maxScroll > 0 ? Math.min(scrollTop / maxScroll, 1) : 0;
        const travel = container2.clientHeight * 0.7;
        bigSf.style.transform = `translateY(${scrollTop * 0.55}px) rotate(${scrollTop * 0.08}deg)`;
        bigSf.style.opacity = String(Math.max(0.04, 0.22 - progress * 0.18));
      });
    };
    if (container2) {
      container2.addEventListener("scroll", onScroll, { passive: true });
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(scrollRaf);
      window.removeEventListener("resize", onResize);
      if (container2) {
        container2.removeEventListener("scroll", onScroll);
      }
    };
  }, [tick]);

  return (
    <div className="landing" ref={containerRef}>
      <canvas className="landing__canvas" ref={canvasRef} />

      <div className="landing__big-snowflake" ref={bigSfRef}>
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
            <line x1="50" y1="2" x2="50" y2="98" />
            <line x1="2" y1="50" x2="98" y2="50" />
            <line x1="15.4" y1="15.4" x2="84.6" y2="84.6" />
            <line x1="84.6" y1="15.4" x2="15.4" y2="84.6" />
            <line x1="50" y1="2" x2="42" y2="15" /><line x1="50" y1="2" x2="58" y2="15" />
            <line x1="50" y1="98" x2="42" y2="85" /><line x1="50" y1="98" x2="58" y2="85" />
            <line x1="2" y1="50" x2="15" y2="42" /><line x1="2" y1="50" x2="15" y2="58" />
            <line x1="98" y1="50" x2="85" y2="42" /><line x1="98" y1="50" x2="85" y2="58" />
            <line x1="15.4" y1="15.4" x2="22" y2="27" /><line x1="15.4" y1="15.4" x2="27" y2="22" />
            <line x1="84.6" y1="84.6" x2="78" y2="73" /><line x1="84.6" y1="84.6" x2="73" y2="78" />
            <line x1="84.6" y1="15.4" x2="73" y2="22" /><line x1="84.6" y1="15.4" x2="78" y2="27" />
            <line x1="15.4" y1="84.6" x2="27" y2="78" /><line x1="15.4" y1="84.6" x2="22" y2="73" />
          </g>
        </svg>
      </div>

      <div className="landing__frost landing__frost--tl" />
      <div className="landing__frost landing__frost--br" />

      <div className="landing__content">
        <section className="landing__hero">
          <div className="landing__hero-left">
            <h1 className="landing__title">
              Snowcord
            </h1>
            <p className="landing__tagline">
              encrypted chat.
              <br />
              nothing else.
            </p>
            <p className="landing__sub">
              No ads. No tracking. No data selling. Your messages are encrypted end-to-end with Curve25519 &mdash; by default, always.
            </p>
            <div className="landing__actions">
              <button
                className="landing__cta-btn"
                onClick={() => navigate("/register")}
              >
                start now
                <ArrowRight size={16} />
              </button>
              <button
                className="landing__ghost-btn"
                onClick={() => navigate("/login")}
              >
                login
              </button>
            </div>
          </div>
          <div className="landing__hero-right">
            <div className="landing__mock-chat">
              <div className="landing__mock-header">
                <div className="landing__mock-dot landing__mock-dot--green" />
                <span>encrypted channel</span>
              </div>
              <div className="landing__mock-line">
                <div className="landing__mock-avatar" />
                <div className="landing__mock-bubble">
                  keys exchanged. session started.
                </div>
              </div>
              <div className="landing__mock-line landing__mock-line--self">
                <div className="landing__mock-bubble landing__mock-bubble--self">
                  verified. end-to-end.
                </div>
              </div>
              <div className="landing__mock-line">
                <div className="landing__mock-avatar" />
                <div className="landing__mock-bubble">
                  zero read by server. ever.
                </div>
              </div>
              <div className="landing__mock-line landing__mock-line--self">
                <div className="landing__mock-bubble landing__mock-bubble--self">
                  that's the point.
                </div>
              </div>
              <div className="landing__mock-lock">
                <Lock size={10} />
                <span>messages are end-to-end encrypted</span>
              </div>
            </div>
          </div>
        </section>

        <div className="landing__divider" />

        <section className="landing__about">
          <div className="landing__about-item">
            <Eye size={18} />
            <div>
              <h3>zero tracking</h3>
              <p>no analytics, no engagement metrics, no data sold. your conversations are invisible to everyone except who you send them to.</p>
            </div>
          </div>
          <div className="landing__about-item">
            <Lock size={18} />
            <div>
              <h3>e2e by default</h3>
              <p>every DM and group chat is encrypted with Curve25519. not even we can read your messages. keys stay on your device.</p>
            </div>
          </div>
          <div className="landing__about-item">
            <Radio size={18} />
            <div>
              <h3>voice & video</h3>
              <p>real-time calls powered by LiveKit, encrypted in transit. servers, channels, roles &mdash; the whole thing. just private.</p>
            </div>
          </div>
        </section>

        <div className="landing__divider" />

        <section className="landing__bottom">
          <p className="landing__bottom-text">built different. built cold.</p>
          <button
            className="landing__cta-btn"
            onClick={() => navigate("/register")}
          >
            get in
            <ArrowRight size={16} />
          </button>
        </section>

        <footer className="landing__footer">
          <span>snowcord</span>
          <span>&copy; {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
}
